import { RegulatorAccValEventType } from '@ps/platform-message-framework/regulator/RegulatorAccValEventType';
import { DeAddressVerificationResult } from '@ps/web-core-enums';
import { FORM_ERROR } from 'final-form';
import type { SubmissionErrors } from 'final-form';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InfoMessage from '@swf/components/InfoMessage/InfoMessage';
import useRequest from '@swf/core/hooks/useRequest';
import { type Dispatch, select } from '@swf/core/store';

import type { AddressFormValues } from '../AddressForm/useAddressUpdate';
import { useAddressUpdate } from '../AddressForm/useAddressUpdate';

import {
  TXTCLI_Something_went_wrongX_please_t_ELL,
  TXTCLI_You_have_successfully_verified_ELL_1
} from './useOnCompleteAttestation.messages.json';

interface CompletionResponse {
  deAddressVerificationResult: DeAddressVerificationResult;
}

interface UseOnCompleteAttestationParams {
  setMessageNode?: (node: React.ReactNode) => void;
  setResult?: (result: 'success' | 'fail') => void;
  failMessage?: React.ReactNode;
}

export const agreementId = 'agreement';

const useOnCompleteAttestation = ({
  setMessageNode,
  setResult,
  failMessage = null
}: UseOnCompleteAttestationParams) => {
  const request = useRequest();
  const {
    account: { updateAccount, update }
  } = useDispatch<Dispatch>();

  const onAddressUpdate = useAddressUpdate(updateAccount);
  const isDeAttestationSuspended = useSelector(select.account.isDeAttestationSuspended);

  return async (values: AddressFormValues): Promise<SubmissionErrors> => {
    const { [agreementId]: agreement, fullName, ...rest } = values;
    const updateErrors = await onAddressUpdate(rest);

    if (isDeAttestationSuspended) {
      setMessageNode?.(null);
      return updateErrors;
    }

    const response = await request<CompletionResponse>({
      service: '/api/v1-preview/account/annual-attestation',
      method: 'POST'
    }).toPromise();

    switch (response?.body?.deAddressVerificationResult) {
      case DeAddressVerificationResult.Success: {
        setMessageNode?.(
          <InfoMessage
            type="confirmation"
            messageTitle={TXTCLI_You_have_successfully_verified_ELL_1}
          />
        );
        setResult?.('success');
        update({ accValEventType: undefined });
        return updateErrors;
      }
      case DeAddressVerificationResult.Failed: {
        setMessageNode?.(failMessage);
        setResult?.('fail');
        update({ accValEventType: RegulatorAccValEventType.SUSPENDED_BY_ANN_ATT });
        return updateErrors;
      }
      default: {
        setMessageNode?.(null);
        return {
          ...updateErrors,
          [FORM_ERROR]: (
            <InfoMessage type="error" messageTitle={TXTCLI_Something_went_wrongX_please_t_ELL} />
          )
        };
      }
    }
  };
};

export default useOnCompleteAttestation;
